import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  Stack,
  Slider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  TablePagination,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import {
  GetSupplyRiskHeatMap,
  setState,
  getProductsByCountry,
} from "../analyzeSlice";
import { sizes, Apis } from "../analyzeUtils";

const ZERO_DIFF_COLOR = "#FFF59D"; // light yellow at the threshold
/** Absolute distance from threshold where green/red reach full intensity. */
const COLOR_FULL_AT = 40;
// Threshold range (percent). Allow up to 200% to accommodate large risk percentages.
const THRESHOLD_MIN = 0;
const THRESHOLD_MAX = 200;
const DEFAULT_ROWS_PER_PAGE = 10;
const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];
const SELECT_ALL_PRODUCTS = "__SELECT_ALL_PRODUCTS__";
const SELECT_ALL_PRODUCT_FAMILIES = "__SELECT_ALL_PRODUCT_FAMILIES__";

function clamp01(t) {
  return Math.max(0, Math.min(1, t));
}

function hslToHex(h, s, l) {
  const sat = Math.max(0, Math.min(100, s)) / 100;
  const light = Math.max(0, Math.min(100, l)) / 100;
  const a = sat * Math.min(light, 1 - light);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = light - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Color by how far difference % is from the user threshold.
 * At threshold → light yellow; below → green; above → red.
 */
function percentToFillColor(percent, threshold = 0) {
  const v = Number(percent) - Number(threshold);
  if (!Number.isFinite(v) || Math.abs(v) < 1e-9) {
    return ZERO_DIFF_COLOR;
  }

  const mag = Math.min(COLOR_FULL_AT, Math.abs(v));
  const t = clamp01(mag / COLOR_FULL_AT);

  // New behavior: values above threshold -> green (safe), values below threshold -> amber -> red (risk)
  if (v > 0) {
    // Above threshold => greener as v grows
    const lightness = 78 - t * 42;
    const saturation = 65 + t * 25;
    return hslToHex(140, saturation, lightness);
  }

  // Below threshold => amber near threshold, redden as magnitude increases
  const lightness = 78 - t * 40;
  const saturation = 80 + t * 18;
  const hue = 40 - t * 38; // interpolate from amber (~40) to red (~2)
  return hslToHex(hue, saturation, lightness);
}

function formatDiffPercent(percent) {
  const v = Number(percent);
  if (!Number.isFinite(v)) return "0%";
  const rounded = Math.round(v);
  if (rounded > 0) return `+${rounded}%`;
  return `${rounded}%`;
}

function truncateSkuLabel(val, maxChars = 36) {
  const s = String(val ?? "");
  if (s.length <= maxChars) return s;
  return `${s.slice(0, maxChars - 1)}…`;
}

const Y_LABEL_WIDTH = 280;
const Y_LABEL_GAP = 20;

function calcDiffPercent(supply, demand, fallback) {
  const s = Number(supply);
  const d = Number(demand);
  if (Number.isFinite(s) && s !== 0 && Number.isFinite(d)) {
    return ((d - s) * 100) / s;
  }
  if (Number.isFinite(s) && s === 0 && Number.isFinite(d) && d > 0) {
    return 100;
  }
  const fb = Number(fallback);
  return Number.isFinite(fb) ? fb : 0;
}

/**
 * Apex heatmap colors ONLY via colorScale.ranges.
 * Pivot (light yellow) is at `threshold`; shades follow distance from it.
 */
function buildColorScaleRanges(threshold = 0) {
  const pivot = Number(threshold) || 0;
  const lo = Math.floor(pivot - COLOR_FULL_AT);
  const hi = Math.ceil(pivot + COLOR_FULL_AT);

  const ranges = [
    {
      from: -1e9,
      to: lo - 0.5,
      color: percentToFillColor(lo, pivot),
      name: "safe_lo",
    },
  ];

  for (let i = lo; i <= hi; i++) {
    ranges.push({
      from: i - 0.5,
      to: i === hi ? hi + 0.5 : i + 0.499999,
      color: percentToFillColor(i, pivot),
      name: `pct_${i}`,
    });
  }

  ranges.push({
    from: hi + 0.500001,
    to: 1e9,
    color: percentToFillColor(hi, pivot),
    name: "risk_hi",
  });

  return ranges;
}

function buildHeatmapSeries(payload) {
  if (!payload?.rows?.length || !payload?.months?.length) {
    return [];
  }

  return payload.rows.map((row) => ({
    name: row.packName || row.clientPackCode || String(row.clientPackCode ?? ""),
    data: (row.cells || []).map((cell) => {
      // Accept either PascalCase (server-side) or camelCase (API JSON) property names
      const finalForecastRaw =
        cell.finalUnconstrainedForecastunits ?? cell.FinalUnconstrainedForecastunits;
      const openCustomerOrderRaw = cell.openCustomerOrder ?? cell.OpenCustomerOrder;
      const usableInventoryRaw = cell.usableInventory ?? cell.UsableInventory;
      const confirmedInboundSupplyRaw =
        cell.confirmedInboundSupply ?? cell.ConfirmedInboundSupply;
      const diffPercentRaw = cell.riskPercent ?? cell.RiskPercent ?? cell.riskPercent;

      const finalForecast = Number(finalForecastRaw) || 0;
      const openCustomerOrder = Number(openCustomerOrderRaw) || 0;
      const usableInventory = Number(usableInventoryRaw) || 0;
      const confirmedInboundSupply = Number(confirmedInboundSupplyRaw) || 0;
      const diffPercent = Number(diffPercentRaw) || 0;

      const periodLabel = cell.periodLabel ?? cell.PeriodLabel ?? cell.period ?? "";

      return {
        x: periodLabel,
        y: diffPercent,
        finalForecast,
        openCustomerOrder,
        usableInventory,
        confirmedInboundSupply,
        diffPercent,
      };
    }),
  }));
}

function RiskGradientLegend({ threshold }) {
  const label = formatDiffPercent(threshold);
  return (
    <Box sx={{ minWidth: { xs: "100%", sm: 280 }, maxWidth: 360 }}>
      <Box
        sx={{
          height: 14,
          borderRadius: 999,
          // Gradient: left = red (risk when < threshold), middle = threshold (amber/yellow), right = green (safe when > threshold)
          background:
            "linear-gradient(90deg, #D32F2F 0%, #EF5350 24%, #FF8F00 40%, #FFF59D 50%, #66BB6A 100%)",
          border: "1px solid rgba(0,0,0,0.12)",
        }}
      />
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ mt: 0.5, px: 0.25 }}
      >
        <Typography variant="caption" color="text.secondary">
          Risk (&lt;{label})
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Safe (&gt;{label})
        </Typography>
      </Stack>
    </Box>
  );
}

function ThresholdControl({ value, onChange }) {
  return (
    <Box sx={{ minWidth: { xs: "100%", sm: 320 }, maxWidth: 420, px: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Risk threshold: {formatDiffPercent(value)}
      </Typography>
      <Slider
        value={value}
        min={THRESHOLD_MIN}
        max={THRESHOLD_MAX}
        step={1}
        marks={[
          { value: THRESHOLD_MIN, label: `${THRESHOLD_MIN}%` },
          { value: 100, label: "100%" },
          { value: THRESHOLD_MAX, label: `${THRESHOLD_MAX}%` },
        ]}
        valueLabelDisplay="auto"
        valueLabelFormat={(v) => formatDiffPercent(v)}
        onChange={(_, next) => onChange(next)}
        sx={{
          color: "#D32F2F",
          "& .MuiSlider-rail": {
            // rail gradient: left = red (risk when below threshold), middle = threshold, right = green (safe when above threshold)
            background: "linear-gradient(90deg, #D32F2F 0%, #FFF59D 50%, #66BB6A 100%)",
            opacity: 1,
          },
          "& .MuiSlider-track": {
            background: "transparent",
            border: "none",
          },
        }}
      />
      <Typography variant="caption" color="text.secondary">
        Light yellow = threshold. Left = risk (red). Right = safe (green).
      </Typography>
    </Box>
  );
}

export default function SupplyRiskHeatMap() {
  const dispatch = useDispatch();
  const { isSupplyRiskHeatMapLoading, supplyRiskHeatMap } = useSelector(
    (store) => store.analyze
  );
  const { globalSelectedCountryID, globalSelectedCountryName } = useSelector(
    (store) => store.login
  );

  const {
    countries,
    selectedCountry,
    selectedCountryID,
    tradeProducts,
    productSelected,
  } = useSelector((store) => store.analyze);

  const selectWidth = sizes.w200;
  const selectHeight = sizes.h35;

  // Default risk threshold set to 100%
  const [threshold, setThreshold] = useState(100);
  const [productFamilies, setProductFamilies] = useState([]);
  const [productFamilySelected, setProductFamilySelected] = useState([]);
  const [requestAllProductFamilies, setRequestAllProductFamilies] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [requestAllProducts, setRequestAllProducts] = useState(false);
  const lastHeatMapRequest = useRef(null);
  const selectedProducts = Array.isArray(productSelected) ? productSelected : [];
  const allProductsSelected =
    tradeProducts.length > 0 &&
    selectedProducts.length === tradeProducts.length &&
    tradeProducts.every((product) => selectedProducts.includes(product));

  useEffect(() => {
    // prefer explicit selectedCountryID (user choice) otherwise fall back to global
    const geoId = selectedCountryID || globalSelectedCountryID || null;
    const productNames =
      selectedProducts.length > 0 && !requestAllProducts ? selectedProducts : null;
    const productFamiliesReq =
      productFamilySelected.length > 0 && !requestAllProductFamilies
        ? productFamilySelected
        : null;

    const requestKey = JSON.stringify({ geoId, productNames, productFamiliesReq });
    if (lastHeatMapRequest.current === requestKey) return;
    lastHeatMapRequest.current = requestKey;

    dispatch(
      GetSupplyRiskHeatMap({
        geoId,
        productNames,
        productFamilies: productFamiliesReq,
      })
    );
  }, [
    dispatch,
    selectedCountryID,
    globalSelectedCountryID,
    productSelected,
    requestAllProducts,
    productFamilySelected,
    requestAllProductFamilies,
  ]);

  // When country changes (user selection or global change), fetch products for that country
  useEffect(() => {
    // Prefer explicit selectedCountryID (including 0 for "All"); fall back to globalSelectedCountryID.
    const countryId =
      selectedCountryID !== "" && selectedCountryID !== null && selectedCountryID !== undefined
        ? selectedCountryID
        : globalSelectedCountryID !== null && globalSelectedCountryID !== undefined
        ? globalSelectedCountryID
        : null;
    if (countryId !== null) {
      dispatch(getProductsByCountry({ countryID: countryId }));
    }
  }, [dispatch, selectedCountryID, globalSelectedCountryID]);

  // keep selected country in sync with global selection on login changes
  useEffect(() => {
    if (globalSelectedCountryID !== null && globalSelectedCountryName) {
      dispatch(setState("selectedCountryID", globalSelectedCountryID));
      dispatch(setState("selectedCountry", globalSelectedCountryName));
    }
  }, [dispatch, globalSelectedCountryID, globalSelectedCountryName]);

  function handleCountryChange(event) {
    const name = event.target.value;
    // When 'All' is selected, use 0 so backend treats it as no GeoID filter (DB layer maps 0 to NULL)
    let countryID = 0;
    if (name !== "All") {
      const match = countries?.find((c) => c.countryName === name);
      countryID = match ? match.countryID : "";
    }
    dispatch(setState("selectedCountry", name));
    dispatch(setState("selectedCountryID", countryID));
    dispatch(setState("productSelected", []));
    setRequestAllProducts(false);
  }

  function handleProductChange(event) {
    const value =
      typeof event.target.value === "string"
        ? event.target.value.split(",")
        : event.target.value;

    if (value.includes(SELECT_ALL_PRODUCTS)) {
      const selectAll = !allProductsSelected;
      setRequestAllProducts(selectAll);
      dispatch(
        setState(
          "productSelected",
          selectAll ? tradeProducts : []
        )
      );
      return;
    }

    const selectAllProducts =
      tradeProducts.length > 0 &&
      value.length === tradeProducts.length &&
      tradeProducts.every((product) => value.includes(product));
    setRequestAllProducts(selectAllProducts);
    dispatch(
      setState("productSelected", value)
    );
    // also trigger immediate reload of product families for this new selection (handles timing when country = All)
    try {
      const countryId =
        selectedCountryID !== "" && selectedCountryID !== null && selectedCountryID !== undefined
          ? selectedCountryID
          : globalSelectedCountryID !== null && globalSelectedCountryID !== undefined
          ? globalSelectedCountryID
          : null;
      const products = value.length > 0 && !selectAllProducts ? value : null;
      // call loader function (defined below)
      loadProductFamilies(countryId, products);
    } catch (err) {
      // ignore
    }
  }

  function handleProductFamilyChange(event) {
    const value =
      typeof event.target.value === "string"
        ? event.target.value.split(",")
        : event.target.value;

    if (value.includes(SELECT_ALL_PRODUCT_FAMILIES)) {
      const selectAll = !requestAllProductFamilies;
      setRequestAllProductFamilies(selectAll);
      setProductFamilySelected(selectAll ? productFamilies : []);
      return;
    }

    const selectAllFamilies =
      productFamilies.length > 0 &&
      value.length === productFamilies.length &&
      productFamilies.every((p) => value.includes(p));
    setRequestAllProductFamilies(selectAllFamilies);
    setProductFamilySelected(value);
  }

  const series = useMemo(
    () => buildHeatmapSeries(supplyRiskHeatMap),
    [supplyRiskHeatMap]
  );

  useEffect(() => {
    setPage(0);
  }, [series]);

  // Load product families whenever country or selected products change
  async function loadProductFamilies(countryId, products) {
    try {
      const data = JSON.parse(localStorage.getItem("data"));
      const token = data.token;
      const url =
        Apis.getProductsFamilyByProducts +
        (countryId != null && countryId !== "" ? `CountryID=${countryId}` : "");
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: JSON.stringify(products),
      });
      const jsonData = await response.json();
      setProductFamilies(Array.isArray(jsonData) ? jsonData : []);
      // reset selection when families list changes
      setProductFamilySelected([]);
      setRequestAllProductFamilies(false);
    } catch (error) {
      console.error("Error loading product families", error);
      setProductFamilies([]);
    }
  }

  useEffect(() => {
    const countryId =
      selectedCountryID !== "" && selectedCountryID !== null && selectedCountryID !== undefined
        ? selectedCountryID
        : globalSelectedCountryID !== null && globalSelectedCountryID !== undefined
        ? globalSelectedCountryID
        : null;
    const products =
      selectedProducts.length > 0 && !requestAllProducts ? selectedProducts : null;
    loadProductFamilies(countryId, products);
  }, [selectedCountryID, globalSelectedCountryID, selectedProducts, requestAllProducts]);

  const pageCount = Math.ceil(series.length / rowsPerPage);
  const currentPage = Math.min(page, Math.max(0, pageCount - 1));
  const paginatedSeries = useMemo(
    () =>
      series.slice(
        currentPage * rowsPerPage,
        (currentPage + 1) * rowsPerPage
      ),
    [currentPage, rowsPerPage, series]
  );

  const colorScaleRanges = useMemo(
    () => buildColorScaleRanges(threshold),
    [threshold]
  );

  const chartHeight = useMemo(() => {
    const rowCount = paginatedSeries.length || 1;
    return Math.max(420, Math.min(1400, 100 + rowCount * 42));
  }, [paginatedSeries.length]);

  const options = useMemo(
    () => ({
      chart: {
        type: "heatmap",
        toolbar: { show: true },
        animations: { enabled: false },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        width: 0,
        colors: ["transparent"],
      },
      plotOptions: {
        heatmap: {
          radius: 0,
          enableShades: false,
          shadeIntensity: 0,
          useFillColorAsStroke: true,
          reverseNegativeShade: false,
          colorScale: {
            ranges: colorScaleRanges,
          },
        },
      },
      xaxis: {
        type: "category",
        position: "top",
        labels: {
          show: true,
          rotate: 0,
          rotateAlways: false,
          hideOverlappingLabels: false,
          trim: false,
          style: { fontSize: "10px" },
        },
        axisBorder: { show: true },
        axisTicks: { show: true },
        tooltip: { enabled: false },
      },
      yaxis: {
        labels: {
          show: true,
          align: "left",
          minWidth: Y_LABEL_WIDTH,
          maxWidth: Y_LABEL_WIDTH,
          offsetX: Y_LABEL_GAP,
          style: { fontSize: "11px" },
          formatter: (val) => truncateSkuLabel(val),
        },
      },
      grid: {
        show: false,
        padding: {
          top: 12,
          left: Y_LABEL_GAP,
        },
      },
      legend: {
        show: false,
      },
      tooltip: {
        enabled: true,
        custom: ({ seriesIndex, dataPointIndex, w }) => {
          const point =
            w?.config?.series?.[seriesIndex]?.data?.[dataPointIndex] || {};
          const sku = w?.config?.series?.[seriesIndex]?.name || "";
          const month = point.x || "";
          const finalForecast = Number(point.finalForecast);
          const openCustomerOrder = Number(point.openCustomerOrder);
          const usableInventory = Number(point.usableInventory);
          const confirmedInboundSupply = Number(point.confirmedInboundSupply);
          const diffPct = Number(point.diffPercent ?? point.y) || 0;
          const delta = diffPct - threshold;
          const status =
            delta < -1e-9
              ? `Risk (below ${formatDiffPercent(threshold)} threshold)`
              : Math.abs(delta) < 1e-9
                ? `At threshold (${formatDiffPercent(threshold)})`
                : `Safe (above ${formatDiffPercent(threshold)} threshold)`;
          return `<div style="padding:10px 12px;font-size:12px;line-height:1.45;min-width:220px;">
            <div><strong>${sku}</strong></div>
            <div style="color:#607d8b;margin-bottom:6px;">${month}</div>
            <div>Final Unconstrained Forecast: ${Number.isFinite(finalForecast) ? finalForecast : "-"}</div>
            <div>Open Customer Order: ${Number.isFinite(openCustomerOrder) ? openCustomerOrder : "-"}</div>
            <div>Usable Inventory: ${Number.isFinite(usableInventory) ? usableInventory : "-"}</div>
            <div>Confirmed Inbound Supply: ${Number.isFinite(confirmedInboundSupply) ? confirmedInboundSupply : "-"}</div>
            <div style="margin-top:8px;padding-top:8px;border-top:1px solid #eceff1;">
              <strong>Supply Coverage %: ${formatDiffPercent(diffPct)}</strong>
            </div>
            <div style="color:#78909c;margin-top:2px;">Threshold: ${formatDiffPercent(threshold)}</div>
            <div style="color:#78909c;margin-top:2px;">${status}</div>
            <div style="color:#78909c;margin-top:6px;font-size:11px;">
              Supply Coverage % = (Opening Usable Inventory + Confirmed Inbound Supply) ÷ (Unconstrained Forecast × (1 + 5% Safety Stock))
            </div>
          </div>`;
        },
      },
    }),
    [colorScaleRanges, threshold]
  );

  return (
    <Box sx={{ mt: 3, px: 1 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ md: "flex-start" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6">Supply Risk Heat Map</Typography>
          {globalSelectedCountryName ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {globalSelectedCountryName}
            </Typography>
          ) : null}
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={3}
          alignItems={{ sm: "center" }}
          sx={{ flex: 1, justifyContent: "flex-end" }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" sx={{ mr: 1 }}>
            <FormControl size="small" sx={{ width: selectWidth }}>
              <InputLabel>Country</InputLabel>
              <Select value={selectedCountry || ""} label="Country" onChange={handleCountryChange} sx={{ height: selectHeight }}>
                <MenuItem value={"All"}>All</MenuItem>
                {countries?.map((country, idx) => (
                  <MenuItem key={idx} value={country.countryName}>
                    {country.countryName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ width: selectWidth }}>
              <InputLabel>Product</InputLabel>
              <Select
                multiple
                value={selectedProducts}
                label="Product"
                onChange={handleProductChange}
                input={<OutlinedInput label="Product" />}
                renderValue={(selected) =>
                  selected.length === 0 ? "All" : selected.join(", ")
                }
                sx={{ minHeight: selectHeight }}
              >
                <MenuItem value={SELECT_ALL_PRODUCTS}>
                  <Checkbox
                    checked={allProductsSelected}
                    indeterminate={
                      selectedProducts.length > 0 && !allProductsSelected
                    }
                  />
                  <ListItemText primary="Select All" />
                </MenuItem>
                {tradeProducts?.map((p, idx) => (
                  <MenuItem key={idx} value={p}>
                    <Checkbox checked={selectedProducts.indexOf(p) > -1} />
                    <ListItemText primary={p} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ width: selectWidth }}>
              <InputLabel>Product Family</InputLabel>
              <Select
                multiple
                value={productFamilySelected}
                label="Product Family"
                onChange={handleProductFamilyChange}
                input={<OutlinedInput label="Product Family" />}
                renderValue={(selected) =>
                  selected.length === 0 ? "All" : selected.join(", ")
                }
                sx={{ minHeight: selectHeight }}
              >
                <MenuItem value={SELECT_ALL_PRODUCT_FAMILIES}>
                  <Checkbox
                    checked={requestAllProductFamilies}
                    indeterminate={
                      productFamilySelected.length > 0 && !requestAllProductFamilies
                    }
                  />
                  <ListItemText primary="Select All" />
                </MenuItem>
                {productFamilies?.map((p, idx) => (
                  <MenuItem key={idx} value={p}>
                    <Checkbox checked={productFamilySelected.indexOf(p) > -1} />
                    <ListItemText primary={p} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <ThresholdControl value={threshold} onChange={setThreshold} />
          <RiskGradientLegend threshold={threshold} />
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, position: "relative", minHeight: 320 }}>
        {isSupplyRiskHeatMapLoading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.55)",
              zIndex: 2,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {!isSupplyRiskHeatMapLoading && series.length === 0 && (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <Typography color="text.secondary">
              No supply / AI forecast data found for the selected country and
              period window.
            </Typography>
          </Box>
        )}

        {series.length > 0 && (
          <Box
            sx={{
              "& .apexcharts-yaxis text": {
                textAnchor: "start !important",
              },
              "& .apexcharts-yaxis-texts-g": {
                transform: `translateX(-${Y_LABEL_WIDTH - Y_LABEL_GAP}px)`,
              },
            }}
          >
            <Chart
              key={`srhm-${currentPage}-${paginatedSeries.length}-${threshold}-${globalSelectedCountryID || "all"}`}
              options={options}
              series={paginatedSeries}
              type="heatmap"
              height={chartHeight}
              width="100%"
            />
          </Box>
        )}

        {series.length > 0 && (
          <TablePagination
            component="div"
            count={series.length}
            page={currentPage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              mt: 1,
              ".MuiTablePagination-toolbar": {
                minHeight: 48,
                px: { xs: 0, sm: 2 },
              },
            }}
          />
        )}
      </Paper>
    </Box>
  );
}
